/**
 * Service to track user activity and manage login rewards
 */

import { apiService, TrackLoginResponse } from './api';
import { parseApiError } from '@/utils/apiErrorHandler';
import {
  createTrackLoginRequest,
  shouldShowOnboarding as shouldShowOnboardingFromTrackLogin,
  shouldShowRewardPopup as shouldShowRewardPopupFromTrackLogin,
} from '@shared/utils/trackLoginGates';

export class UserActivityService {
  /**
   * Records a user login and checks for reward eligibility using the API
   * @param userId The user's unique identifier
   * @param token Authentication token
   * @returns Object containing reward eligibility information
   */
  static async recordLogin(userId: string, token?: string): Promise<TrackLoginResponse> {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const params = createTrackLoginRequest(userId);

      const response = await apiService.trackLogin(params, token || '');
      
      // Log the response for debugging
      console.log('Login tracking response:', response);
      
      return response;
    } catch (error) {
      const parsed = parseApiError(error);
      if (parsed.type === 'network') {
        console.debug('Login tracking skipped (API unreachable)');
      } else {
        console.error('Failed to track login:', error);
      }
      throw error;
    }
  }

  /**
   * Checks if user should see onboarding based on login response
   * @param loginResponse The response from trackLogin API
   * @returns Object indicating onboarding status
   */
  static shouldShowOnboarding(loginResponse: TrackLoginResponse): {
    showOnboarding: boolean;
    isFirstLogin: boolean;
    isThirdDay: boolean;
    dotsAwarded: number;
  } {
    return shouldShowOnboardingFromTrackLogin(loginResponse);
  }

  /**
   * Checks if user should see reward popup based on login response
   * @param loginResponse The response from trackLogin API
   * @returns Object indicating reward popup status
   */
  static shouldShowRewardPopup(loginResponse: TrackLoginResponse): {
    showReward: boolean;
    isThirdDay: boolean;
    dotsAwarded: number;
    currentStreak: number;
  } {
    return shouldShowRewardPopupFromTrackLogin(loginResponse);
  }
}
